import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../config/axios';
import { Pencil, Trash2, ChevronDown, Search, Copy, Check, MessageCircle, Download } from 'lucide-react';
import { getSiteClusterDisplay } from '../utils/siteDisplay.js';
import './SiteManagement.css';
import { EXPORT_FAILED_MESSAGE, getFriendlyErrorMessage } from '../utils/errorMessages';

const REQUIRED_FIELDS = ['name', 'email', 'buCode', 'brandId', 'managerId'];
const FIRST_CLASS_OPTIONAL_FIELDS = new Set(['cluster']);

function getUnionAddedFields(sites) {
  const union = new Set();
  for (const s of sites || []) {
    const af = s.addedFields;
    const arr = Array.isArray(af) ? af : (typeof af === 'string' ? (() => { try { return JSON.parse(af); } catch { return []; } })() : []);
    arr.forEach(k => { if (k && String(k).trim()) union.add(String(k).trim().toLowerCase()); });
  }
  return [...union].sort();
}

function getExtrasValue(site, key) {
  const ex = site?.extras;
  const obj = ex && typeof ex === 'object' ? ex : (typeof ex === 'string' ? (() => { try { return JSON.parse(ex || '{}'); } catch { return {}; } })() : {});
  const v = obj[key];
  if (v != null) return String(v);
  const legacy = site?.[key];
  return legacy != null ? String(legacy) : '';
}

const BRAND_COLORS = [
  { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  { bg: '#f0fdfa', color: '#0d9488', border: '#99f6e4' },
  { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  { bg: '#faf5ff', color: '#9333ea', border: '#e9d5ff' },
  { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  { bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
  { bg: '#fdf2f8', color: '#db2777', border: '#fbcfe8' },
  { bg: '#f5f3ff', color: '#7c3aed', border: '#ddd6fe' },
];

function getBrandColor(brandId, allBrandIds) {
  const idx = allBrandIds.indexOf(brandId);
  return BRAND_COLORS[idx >= 0 ? idx % BRAND_COLORS.length : 0];
}

const SiteManagement = ({ sites, users, onCreateSite, onUpdateSite, onDeleteSite, onRefresh, allBrands, onChatSite, canStartChat, isAdmin }) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const formRef = useRef(null);
  const [brands, setBrands] = useState([]);
  const [showBrandForm, setShowBrandForm] = useState(false);
  const [newBrand, setNewBrand] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    buCode: '',
    cluster: '',
    managerId: '',
    brandId: '',
    email: '',
    password: '',
    extras: {}
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterText, setFilterText] = useState('');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [copiedEmail, setCopiedEmail] = useState(null);
  const [sitesExporting, setSitesExporting] = useState(false);

  useEffect(() => {
    fetchBrands();
  }, []);

  useEffect(() => {
    if (editingSite && showCreateForm && formRef.current) {
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [editingSite, showCreateForm]);

  const fetchBrands = async () => {
    try {
      const response = await axiosInstance.get('/api/tenant/brands');
      setBrands(response.data.brands || []);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const handleCreateBrand = async (e) => {
    e.preventDefault();
    if (!newBrand.trim()) {
      setError('Brand name is required');
      return;
    }
    try {
      await axiosInstance.post('/api/tenant/brands', { name: newBrand.trim() });
      setNewBrand('');
      setShowBrandForm(false);
      setSuccess('Brand created successfully');
      fetchBrands();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to create brand');
    }
  };

  const unionAddedFields = getUnionAddedFields(sites).filter((k) => !FIRST_CLASS_OPTIONAL_FIELDS.has(k));

  const emptyFormData = () => ({
    name: '',
    buCode: '',
    cluster: '',
    managerId: '',
    brandId: '',
    email: '',
    password: '',
    extras: {}
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const extrasObj = formData.extras && typeof formData.extras === 'object' ? formData.extras : {};
    const filteredExtras = {};
    for (const [k, v] of Object.entries(extrasObj)) {
      if (v && String(v).trim()) filteredExtras[k] = String(v).trim();
    }

    const dataToSend = {
      name: formData.name,
      buCode: formData.buCode,
      cluster: formData.cluster.trim(),
      email: formData.email,
      managerId: formData.managerId || null,
      brandId: formData.brandId || null,
      extras: filteredExtras,
    };
    if (formData.password && (editingSite ? true : formData.email)) {
      dataToSend.password = formData.password;
    }

    if (editingSite) {
      const result = await onUpdateSite(editingSite.id, dataToSend);
      if (result.success) {
        setSuccess('Site updated successfully');
        setFormData(emptyFormData());
        setEditingSite(null);
        setShowCreateForm(false);
        setTimeout(() => { if (onRefresh) onRefresh(); }, 500);
      } else {
        setError(result.error);
      }
    } else {
      const result = await onCreateSite(dataToSend);
      if (result.success) {
        setSuccess('Site created successfully');
        setFormData(emptyFormData());
        setShowCreateForm(false);
        setTimeout(() => { if (onRefresh) onRefresh(); }, 500);
      } else {
        setError(result.error);
      }
    }
  };

  const handleEdit = (site) => {
    setEditingSite(site);
    const ex = {};
    for (const k of unionAddedFields) {
      ex[k] = getExtrasValue(site, k);
    }
    setFormData({
      name: site.name,
      buCode: site.buCode || '',
      cluster: getSiteClusterDisplay(site),
      managerId: site.managerId || '',
      brandId: site.brandId || '',
      email: site.email || '',
      password: '',
      extras: ex
    });
    setShowCreateForm(true);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (siteId) => {
    if (window.confirm('Are you sure you want to delete this site? This action cannot be undone.')) {
      const result = await onDeleteSite(siteId);
      if (result.success) {
        // handled by parent
      } else {
        alert(result.error);
      }
    }
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingSite(null);
    setFormData(emptyFormData());
    setError('');
    setSuccess('');
  };

  const handleExportSitesExcel = async () => {
    setSitesExporting(true);
    setError('');
    try {
      const base = axiosInstance.defaults.baseURL || '';
      const url = base + '/api/tenant/sites/export-excel';
      const token = localStorage.getItem('token');
      const fetchHeaders = { Authorization: 'Bearer ' + token };
      if (typeof window !== 'undefined' && window.location.host) {
        fetchHeaders['X-Tenant-Host'] = window.location.host;
      }
      const res = await fetch(url, { headers: fetchHeaders });
      if (!res.ok) {
        let errData = null;
        try {
          errData = await res.json();
        } catch (_) {}
        setError(
          getFriendlyErrorMessage(
            { response: { status: res.status, data: errData || { code: 'EXPORT_FAILED' } } },
            { kind: 'export' }
          ) || EXPORT_FAILED_MESSAGE
        );
        return;
      }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'Sites_Export_' + new Date().toISOString().split('T')[0] + '.xlsx';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      console.error(err);
      setError(getFriendlyErrorMessage(err, { kind: 'export' }) || EXPORT_FAILED_MESSAGE);
    } finally {
      setSitesExporting(false);
    }
  };

  const filteredSites = sites.filter(site => {
    if (!filterText) return true;
    const q = filterText.toLowerCase();
    const manager = users.find(u => u.id === site.managerId);
    const brand = brands.find(b => b.id === site.brandId);
    return (
      (site.name || '').toLowerCase().includes(q) ||
      (site.email || '').toLowerCase().includes(q) ||
      (manager?.name || '').toLowerCase().includes(q) ||
      (brand?.name || '').toLowerCase().includes(q) ||
      (site.buCode || '').toLowerCase().includes(q) ||
      getSiteClusterDisplay(site).toLowerCase().includes(q) ||
      new Date(site.createdAt).toLocaleDateString().toLowerCase().includes(q)
    );
  });

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedSites = () => {
    if (!sortColumn) return filteredSites;
    return [...filteredSites].sort((a, b) => {
      let aValue, bValue;
      switch (sortColumn) {
        case 'site':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'email':
          aValue = (a.email || '').toLowerCase();
          bValue = (b.email || '').toLowerCase();
          break;
        case 'cluster':
          aValue = getSiteClusterDisplay(a).toLowerCase();
          bValue = getSiteClusterDisplay(b).toLowerCase();
          break;
        case 'brand':
          const brandA = brands.find(br => br.id === a.brandId);
          const brandB = brands.find(br => br.id === b.brandId);
          aValue = (brandA?.name || '').toLowerCase();
          bValue = (brandB?.name || '').toLowerCase();
          break;
        case 'manager':
          const managerA = users.find(u => u.id === a.managerId);
          const managerB = users.find(u => u.id === b.managerId);
          aValue = (managerA?.name || '').toLowerCase();
          bValue = (managerB?.name || '').toLowerCase();
          break;
        case 'created':
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
          break;
        default:
          return 0;
      }
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortableHeader = ({ column, children }) => (
    <th className="sm-sortable-th" onClick={() => handleSort(column)} title={'Sort by ' + children}>
      <button type="button" className="sm-sort-btn">
        <span>{children}</span>
        <ChevronDown size={10} className={'sm-sort-icon' + (sortColumn === column ? ' active' : '') + (sortColumn === column && sortDirection === 'desc' ? ' desc' : '')} />
      </button>
    </th>
  );

  const displayBrands = allBrands || brands;
  const allBrandIds = displayBrands.map(b => b.id);

  return (
    <div>
      <div className="sm-section-header">
        <div>
          <h2 className="sm-section-title">Site Management</h2>
          <p className="sm-section-subtitle">Manage all sites</p>
        </div>
        <div className="sm-section-header-actions">
          {isAdmin && (
            <button
              type="button"
              className="btn btn-secondary sm-export-btn"
              onClick={handleExportSitesExcel}
              disabled={sitesExporting}
              title="Download all sites as Excel"
            >
              <Download size={16} aria-hidden />
              {sitesExporting ? 'Exporting…' : 'Export to Excel'}
            </button>
          )}
          <button className="btn btn-primary sm-create-btn" onClick={() => {
            if (showCreateForm) { handleCancel(); } else { setShowCreateForm(true); setEditingSite(null); setFormData(emptyFormData()); }
          }}>
            {showCreateForm ? 'Cancel' : 'Add Site'}
          </button>
        </div>
      </div>

      <div className="sm-stats-row">
        <div className="sm-stat-card">
          <span className="sm-stat-label">TOTAL SITES</span>
          <span className="sm-stat-value sm-stat-sites">{sites.length}</span>
        </div>
        <div className="sm-stat-card">
          <span className="sm-stat-label">TOTAL BRANDS</span>
          <span className="sm-stat-value sm-stat-brands">{displayBrands.length}</span>
        </div>
      </div>

      {showCreateForm && (
        <form ref={formRef} onSubmit={handleSubmit} className="create-form card">
          {editingSite && <h4>Edit Site: {editingSite.name}</h4>}
          <div className="form-group">
            <label>Site Name *</label>
            <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
          </div>
          <div className="form-group">
            <label>BU Code *</label>
            <input type="text" value={formData.buCode} onChange={(e) => setFormData({ ...formData, buCode: e.target.value })} placeholder="e.g. BU001" required />
          </div>
          <div className="form-group">
            <label>Cluster</label>
            <input
              type="text"
              value={formData.cluster}
              onChange={(e) => setFormData({ ...formData, cluster: e.target.value })}
              placeholder="e.g. Large, Small"
            />
          </div>
          {unionAddedFields.length > 0 && (
            <div className="form-row">
              {unionAddedFields.map(k => (
                <div key={k} className="form-group">
                  <label>{k.charAt(0).toUpperCase() + k.slice(1)}</label>
                  <input type="text" value={(formData.extras && formData.extras[k]) || ''} onChange={(e) => setFormData({ ...formData, extras: { ...formData.extras, [k]: e.target.value } })} placeholder={k === 'country' ? 'e.g. UK' : k === 'gln' ? 'e.g. 1234567890123' : ''} />
                </div>
              ))}
            </div>
          )}
          <div className="form-row">
            <div className="form-group">
              <label>Email *</label>
              <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="site@example.com" />
              <small style={{ display: 'block', marginTop: '5px', color: 'var(--color-text-muted)' }}>Email address for site login. If provided, password is required.</small>
            </div>
            <div className="form-group">
              <label>Password {editingSite ? '(leave blank to keep current)' : '(Required)'}</label>
              <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder={editingSite && (editingSite.email || formData.email) ? "Leave blank to keep current password" : ""} required={!editingSite} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Brand *</label>
              <div className="select-with-add">
                <select value={formData.brandId} onChange={(e) => setFormData({ ...formData, brandId: e.target.value })}>
                  <option value="">Select Brand</option>
                  {brands.map(brand => (<option key={brand.id} value={brand.id}>{brand.name}</option>))}
                </select>
                <button type="button" className="btn btn-sm btn-link" onClick={() => setShowBrandForm(!showBrandForm)} title="Add new brand">+ Add</button>
              </div>
              {showBrandForm && (
                <div className="add-item-form">
                  <input type="text" placeholder="New brand name" value={newBrand} onChange={(e) => setNewBrand(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleCreateBrand(e)} />
                  <div className="add-item-actions">
                    <button type="button" className="btn btn-sm btn-primary" onClick={handleCreateBrand}>Add</button>
                    <button type="button" className="btn btn-sm btn-secondary" onClick={() => { setShowBrandForm(false); setNewBrand(''); }}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Manager *</label>
              <select value={formData.managerId} onChange={(e) => setFormData({ ...formData, managerId: e.target.value })}>
                <option value="">Select Manager</option>
                {users.map(manager => (<option key={manager.id} value={manager.id}>{manager.name}</option>))}
              </select>
            </div>
          </div>
          {error && <div className="error">{error}</div>}
          {success && <div className="success">{success}</div>}
          <button type="submit" className="btn btn-primary">{editingSite ? 'Update Site' : 'Create Site'}</button>
        </form>
      )}

      {!showCreateForm && error && <div className="error" style={{ marginBottom: '20px' }}>{error}</div>}
      {!showCreateForm && success && <div className="success" style={{ marginBottom: '20px' }}>{success}</div>}

      <div className="card sm-table-card">
        <div className="sm-search-bar">
          <Search size={15} className="sm-search-icon" />
          <input type="text" placeholder="Search by name, email, brand, manager..." className="sm-search-input" value={filterText} onChange={(e) => setFilterText(e.target.value)} />
          {filterText && <button type="button" onClick={() => setFilterText('')} className="sm-search-clear">Clear</button>}
        </div>
        <div className="sites-table-scroll">
          <table className="table sm-table">
            <thead>
              <tr>
                <SortableHeader column="site">Site</SortableHeader>
                <SortableHeader column="email">Email</SortableHeader>
                <SortableHeader column="cluster">Cluster</SortableHeader>
                <SortableHeader column="brand">Brand</SortableHeader>
                <SortableHeader column="manager">Manager</SortableHeader>
                <SortableHeader column="created">Created</SortableHeader>
                <th className="sm-actions-th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {getSortedSites().length === 0 ? (
                <tr>
                  <td colSpan="7" className="sm-empty-state">
                    {sites.length === 0 ? <p>No sites found.</p> : <p>No sites match your search.</p>}
                  </td>
                </tr>
              ) : (
                getSortedSites().map(site => {
                  const manager = users.find(u => u.id === site.managerId);
                  const brand = brands.find(b => b.id === site.brandId);
                  const brandColor = brand ? getBrandColor(brand.id, allBrandIds) : null;
                  return (
                    <tr key={site.id} className="sm-row">
                      <td className="sm-site-cell">
                        <div className="sm-site-text">
                          <span className="sm-site-name">{site.name}</span>
                          {site.buCode && <span className="sm-site-bucode">{site.buCode}</span>}
                        </div>
                      </td>
                      <td className="sm-meta-cell">{site.email ? <span className="sm-email-wrap"><span className="sm-site-email-text">{site.email}</span><button type="button" className="sm-copy-btn" onClick={() => { navigator.clipboard.writeText(site.email); setCopiedEmail(site.id); setTimeout(() => setCopiedEmail(null), 1500); }}>{copiedEmail === site.id ? <Check size={12} /> : <Copy size={12} />}</button></span> : '-'}</td>
                      <td className="sm-meta-cell">{getSiteClusterDisplay(site) || '-'}</td>
                      <td>
                        {brand ? (
                          <span className="sm-brand-pill" style={{ background: brandColor.bg, color: brandColor.color, borderColor: brandColor.border }}>
                            {brand.name}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="sm-meta-cell">{manager ? manager.name : '-'}</td>
                      <td className="sm-meta-cell sm-date-cell">{new Date(site.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div className="sm-actions">
                          {canStartChat && onChatSite && (
                            <button type="button" className="sm-action-btn sm-action-chat" onClick={() => onChatSite(site)} title="Chat">
                              <MessageCircle size={14} />
                            </button>
                          )}
                          {onUpdateSite && (
                            <button type="button" className="sm-action-btn sm-action-edit" onClick={() => handleEdit(site)} title="Edit site">
                              <Pencil size={14} />
                            </button>
                          )}
                          {onDeleteSite && (
                            <button type="button" className="sm-action-btn sm-action-delete" onClick={() => handleDelete(site.id)} title="Delete site">
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SiteManagement;